import AutocompleteSingleSelectField from "@/components/form/components/AutocompleteSingleSelectField";
import InputField from "@/components/InputField";
import TextareaField from "@/components/TextArea";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { useStreamsQuery } from "@/hooks/api/master.queries";
import { notifyError, notifySuccess } from "@/lib/notification";
import { cn } from "@/lib/utils";
import { authService } from "@/services/auth.service";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

const registerSchema = z.object({
  college_name: z.string().min(2, "College name is required").trim(),
  college_email: z.email("Invalid email address").trim(),
  college_phone: z
    .string()
    .min(10, "Phone number must be at least 10 digits")
    .max(15, "Phone number must be at most 15 digits")
    .trim(),
  stream_id: z.string().min(1, "Stream is required").trim(),
  college_address: z.string().min(5, "Address is required").trim(),
  college_website: z.url("Invalid URL").trim().optional(),
  admin_name: z.string().min(2, "Admin name is required").trim(),
  admin_org_email: z.email("Invalid admin organizational email").trim(),
  admin_personal_email: z
    .email("Invalid admin personal email")
    .optional()
    .or(z.literal("")),
  admin_phone: z
    .string()
    .min(10, "Admin phone must be at least 10 digits")
    .max(15, "Admin phone must be at most 15 digits")
    .trim(),
});

type RegisterFormType = z.infer<typeof registerSchema>;

const RegisterCollege = () => {
  const navigate = useNavigate();

  const streamQuery = useStreamsQuery();

  const form = useForm<RegisterFormType>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      college_name: "",
      college_email: "",
      college_phone: "",
      stream_id: "",
      college_address: "",
      college_website: "",
      admin_name: "",
      admin_org_email: "",
      admin_personal_email: undefined,
      admin_phone: "",
    },
  });

  const registerMutation = useMutation({
    mutationFn: async (data: RegisterFormType) => {
      return await authService.registerCollege(data);
    },
    onSuccess: () => {
      notifySuccess(
        "College registered successfully! We will review your application."
      );
      navigate("/login");
    },
    onError: (error: any) => {
      notifyError(
        error.response?.data?.error?.message ||
          error?.message ||
          "Registration failed. Please try again."
      );
    },
  });

  const onSubmit = (data: RegisterFormType) => {
    const payload = { ...data };
    if (!payload.admin_personal_email) {
      delete payload.admin_personal_email;
    }
    registerMutation.mutate(payload);
  };

  return (
    <div className="flex min-h-screen w-screen flex-col items-center justify-center bg-neutral-50 p-6">
      <div className="w-full max-w-2xl rounded-xl border border-neutral-300 bg-white p-8 shadow">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-bold">Register College</h1>
          <p className="mt-2 text-sm text-neutral-500">
            Fill out the details below to complete your registration.
          </p>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* College Details Section */}
            <div>
              <h2 className="mb-4 border-b pb-2 text-lg font-semibold text-neutral-800">
                College Details
              </h2>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {/* Maps to payload: college_name */}
                <InputField
                  type="text"
                  name="college_name"
                  label="College Name"
                  placeholder="Enter college name"
                  disabled={registerMutation.isPending}
                  required
                  autoFocus
                />
                {/* Maps to payload: college_email */}
                <InputField
                  type="email"
                  name="college_email"
                  label="College Email"
                  placeholder="Enter college email"
                  disabled={registerMutation.isPending}
                  required
                />
                {/* Maps to payload: college_phone */}
                <InputField
                  type="tel"
                  name="college_phone"
                  label="College Phone"
                  placeholder="Enter 10-digit phone number"
                  inputMode="numeric"
                  maxLength={10}
                  disabled={registerMutation.isPending}
                  required
                />
                {/* Maps to payload: stream_id */}
                <AutocompleteSingleSelectField
                  name="stream_id"
                  label="Stream"
                  placeholder="Select stream"
                  options={streamQuery.data || []}
                  disabled={registerMutation.isPending || streamQuery.isLoading}
                  required
                />
                {/* Maps to payload: college_website */}
                <InputField
                  type="url"
                  name="college_website"
                  label="College Website"
                  placeholder="https://example.com"
                  disabled={registerMutation.isPending}
                  className={{ itemClass: "md:col-span-2" }}
                />

                {/* Maps to payload: college_address */}
                <TextareaField
                  name="college_address"
                  label="College Address"
                  placeholder="Enter full address"
                  disabled={registerMutation.isPending}
                  className={{ itemClass: "md:col-span-2" }}
                  required
                />
              </div>
            </div>

            {/* Admin Details Section */}
            <div>
              <h2 className="mb-4 border-b pb-2 text-lg font-semibold text-neutral-800">
                Admin Details
              </h2>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {/* Maps to payload: admin_name */}
                <InputField
                  type="text"
                  name="admin_name"
                  label="Admin Name"
                  placeholder="Enter full name"
                  disabled={registerMutation.isPending}
                  required
                />
                {/* Maps to payload: admin_phone */}
                <InputField
                  type="tel"
                  name="admin_phone"
                  label="Admin Phone"
                  placeholder="Enter 10-digit phone number"
                  inputMode="numeric"
                  maxLength={10}
                  disabled={registerMutation.isPending}
                  required
                />
                {/* Maps to payload: admin_org_email */}
                <InputField
                  type="email"
                  name="admin_org_email"
                  label="Org Email"
                  placeholder="admin@college.edu"
                  disabled={registerMutation.isPending}
                  required
                />
                {/* Maps to payload: admin_personal_email */}
                <InputField
                  type="email"
                  name="admin_personal_email"
                  label="Personal Email"
                  placeholder="admin@gmail.com"
                  disabled={registerMutation.isPending}
                />
              </div>
            </div>

            <div className="mt-8 flex flex-col gap-3">
              <Button
                type="submit"
                className={cn(
                  "w-full transition-colors",
                  registerMutation.isPending
                    ? "opacity-50 hover:cursor-not-allowed"
                    : "hover:cursor-pointer"
                )}
                disabled={registerMutation.isPending}
              >
                {registerMutation.isPending
                  ? "Submitting..."
                  : "Submit Registration"}
              </Button>

              <Button
                type="button"
                variant="outline"
                className={cn(
                  "w-full transition-colors",
                  registerMutation.isPending
                    ? "opacity-50 hover:cursor-not-allowed"
                    : "hover:cursor-pointer"
                )}
                onClick={() => navigate("/login")}
                disabled={registerMutation.isPending}
              >
                Back to Login
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};

export default RegisterCollege;
